import { Hero } from '@/components/giveback/Hero';
import HowItWorks from '@/components/giveback/HowItWorks';
import { JoinTheGiveback } from '@/components/giveback/JoinTheGiveback';
import { WhatIsGiveback } from '@/components/giveback/WhatIsGiveback';
import { OurCommunityImpact } from '@/components/giveback/OurCommunityImpact';
import { OurImpact2024 } from '@/components/giveback/OurImpact2024';
import Layout from '@/components/shared/Layout';
import { SEO } from '@/components/shared/SEO';
import type { Config } from '@/pageConfig/giveback';
import { GIVEBACK_LANDING_PAGE } from '@/utils/productAdaPages';
import type React from 'react';
import { PublicBenefit } from '@/components/giveback/PublicBenefit';
import { InternalImpact } from '@/components/giveback/InternalImpact';
import { usePageScroll } from '@lemonade-hq/cdk';
import { useElementLerp } from '@/hooks/useElementLerp';
import styled, { css } from 'styled-components';
import { lerp } from '@lemonade-hq/ts-helpers';
import { isMobile as isMobileUA } from '@lemonade-hq/cdk';

const GivebackContainer = styled.div<{
  transparent: boolean;
  border: boolean;
  whiteLinks: boolean;
}>`
  position: relative;

  header {
    border-bottom-color: ${({ border }) => (border ? '#e5e5e5' : 'transparent')};

    @media (min-width: 1250px) {
      a {
        transition: color 0.2s;
        color: ${({ whiteLinks }) => (whiteLinks ? 'white' : '#4a4a4a')};
      }
    }

    a svg[role='img'] {
      opacity: 1;

      ${({ whiteLinks }) =>
        whiteLinks &&
        css`
          opacity: 0;
          transition: opacity 0.2s;
        `};
    }
  }

  svg path {
    transition: stroke 0.2s;
  }

  ${({ transparent }) =>
    transparent &&
    css`
      header {
        ${transparent &&
        css`
          [role='button'] svg path {
            stroke: white;
          }
        `};
      }
    `}
`;

const HeaderBg = styled.div`
  width: 100%;
  height: 70px;
  background-color: white;
  position: fixed;
  z-index: 99;
  transition: transform 0.2s cubic-bezier(0.22, 0.61, 0.36, 1);
`;

interface GivebackPage2024TemplateProps {
  config: Config;
}

export const GivebackPageTemplate: React.FC<GivebackPage2024TemplateProps> = ({ config }) => {
  const isMobile = isMobileUA();
  const range: [number, number] = isMobile ? [0.0, 0.1] : [0.1, 0.2];
  const borderThreshold = isMobile ? 300 : 350;

  const { absoluteScroll } = usePageScroll();
  const [ref, t] = useElementLerp(absoluteScroll, {
    range,
    clamp: true,
  });

  return (
    <GivebackContainer transparent={t < 1} border={absoluteScroll > borderThreshold} whiteLinks={t < 0.65}>
      <HeaderBg
        style={{
          transform: `matrix(1, 0, 0, 1, 0, ${lerp(-80, 0, t)})`,
        }}
      />
      <Layout
        pt={[0, 0, 0]}
        headerActions={config.layout.headerActions}
        page={GIVEBACK_LANDING_PAGE}
        headerColor={'transparent'}
        fixedHeader={true}
      >
        <SEO {...config.seo} />
        <div ref={ref}>
          <Hero />
        </div>
        <WhatIsGiveback {...config.whatIsGiveback} />
        <PublicBenefit {...config.publicBenefit} />
        <HowItWorks {...config.howItWorks} />
        <OurCommunityImpact {...config.ourCommunityImpact} />
        <OurImpact2024 {...config.ourImpact2024} />
        <InternalImpact {...config.internalImpact} />
        <JoinTheGiveback {...config.joinTheGiveback} />
      </Layout>
    </GivebackContainer>
  );
};
